import { Component, OnInit, ViewEncapsulation } from '@angular/core';

import { debounceTime } from 'rxjs/operators';

import { Coords, MapStyle, MotesPaths } from '../../models';
import { AutoSub, AutoSubOnDestroy, MapService, StyleService } from '../../providers';

/**
 * A button used to toggle between the default (or project) map style and the satellite map style.
 * The button displays a minimap generated from the pending map style and the center of the map.
 * This minimap is generated using the [Static Images API from Mapbox](https://docs.mapbox.com/api/maps/static-images/)
 */
@AutoSubOnDestroy()
@Component({
    selector: 'toggle-map-style',
    templateUrl: './toggle-map-style.component.html',
    styleUrls: ['./toggle-map-style.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToggleMapStyleComponent implements OnInit {
    // Map styles
    public mapStyleSatellite: MapStyle;

    // Map styles: Active and next
    public mapStyleNext: MapStyle;

    // Thumbnail
    public thumbnailUrl: String = 'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/12.5598,55.698,9,0/100x100@2x?logo=false&attribution=false&access_token=pk.eyJ1Ijoic25hemFtaSIsImEiOiJjaW1pMTBpcDEwMDNydmlrdW8yc2RoY29pIn0.R_8cMvgM26cjwwLW7rlBVQ';
    private coords: Coords = new Coords(55.693182, 12.550525);

    constructor(private styleService: StyleService, private mapService: MapService) { }

    async ngOnInit(): Promise<void> {
        // Request the satellite map style
        this.mapStyleSatellite = await this.styleService.getMapStyle('satellite');
        // Wait for the map to load before proceeding
        await this.mapService.awaitInit();
        // Assign active and next map style
        await this.setActiveMapStyle(this.mapService.projectMapStyle);

        // Generate thumbnail images based on the map style of `this.mapStyleNext` and the center coordinates of the map
        AutoSub(this).reg['thumbnailFromPosition'] = this.mapService.watchMapCenter().pipe(
            debounceTime(500)
        ).subscribe((mapCenter) => {
            // Store the `Coords` object in a member variable in order to generate thumbnails from `this.toggleMapStyle()`
            this.coords = mapCenter.coords;
            // Update the thumbnail URL
            this.thumbnailUrl = this.getThumbnailUrl(this.mapStyleNext.style, mapCenter.coords);
        });
    }

    /**
     * Toggle between the default map style and the satellite map style
     */
    public async toggleMapStyle(): Promise<void> {
        // Switch the active/next map style
        await this.setActiveMapStyle(this.mapStyleNext);

        // Update the thumbnail URL
        this.thumbnailUrl = this.getThumbnailUrl(this.mapStyleNext.style, this.coords);
    }

    /**
     * Set active map style and the next map style based on the active style
     */
    private async setActiveMapStyle(mapStyle: MapStyle) {
        // Change the map style
        this.mapService.changeMapStyle(mapStyle);
        this.mapStyleNext = (this.mapService.activeMapStyle.key == 'satellite') ? this.mapService.projectMapStyle : this.mapStyleSatellite;
    }

    /**
     * Generate a thumbnail image based on a Mapbox style URL and a position
     * @param styleUrl a [Mapbox style URL](https://docs.mapbox.com/help/glossary/style-url/)
     * @param coords a `Coords` object with values for latitude and longitude
     * @returns the URL for an image with the specified style and position
     */
    private getThumbnailUrl(styleUrl: string, coords: Coords): string {
        // Mapbox style URLs take the form `mapbox://styles/{username}}/{styleId}, e.g. `mapbox://styles/mapbox/streets-v11`
        // When generating static images, we only need the username and style ID. Hence, we replace `mapbox://styles/` with an empty string
        const style = styleUrl.replace(/^mapbox:\/\/styles\//, '');
        const zoom = this.mapService.map.getZoom();
        const sizeInPx = 100;

        // Return the combined URL
        return `https://api.mapbox.com/styles/v1/${style}/static/${coords.long},${coords.lat},${zoom}/${sizeInPx}x${sizeInPx}@2x?logo=false&attribution=false&access_token=${MotesPaths.MAPBOX_ACCESS_TOKEN}`;
    }
}
